import {appUrl} from '@frontend/AppUrl'
import {trans} from '@frontend/helpers'
import {
    useAccountFlagsStore,
    useAuthStore,
    useTopMenuStore,
    useUiLayoutStore,
} from '@frontend/stores'
import {
    iconBell,
    iconBellWithCircle,
    iconChat,
    iconChatWithCircle,
    iconHeartOutline,
    iconWallet,
} from '@shared/helpers/icons'
import clsx from 'clsx'
import {type MouseEvent} from 'react'
import {
    NavLink,
    useMatch,
} from 'react-router-dom'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {IconHref} from 'swayok-react-mdb-ui-kit/components/Icon/IconHref'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {AppConfig} from '../../../AppConfig'
import {TopNavigationCreateAdButton} from './TopNavigationCreateAdButton'
import {TopNavigationUserDropdownMenu} from './TopNavigationUserDropdownMenu'

interface CabinetButtonsProps {
    noFavorites?: boolean
}

// Размер иконок в заголовке. Макет: 22px и на десктопе, и на мобильной.
const headerIconSize: number = 22

// Набор кнопок связанных с личным кабинетом пользователя.
export function CabinetButtons(props: CabinetButtonsProps) {

    const {
        noFavorites,
    } = props

    const {
        hasNewNotifications,
        hasNewChatMessages,
    } = useAccountFlagsStore()

    const deviceType = useUiLayoutStore(
        state => state.deviceType
    )
    const bootstrapSize = useUiLayoutStore(
        state => state.bootstrapSize
    )
    const topMenuCabinetMode = useTopMenuStore(
        state => state.cabinetMode
    )
    const topMenuAuthBlock = useTopMenuStore(
        state => state.authBlock
    )
    const initUserAuth = useAuthStore(
        state => state.initUserAuthModal
    )
    const authBlock = useTopMenuStore(
        state => state.authBlock
    )
    const createAdButton = useTopMenuStore(
        state => state.createAdButton
    )
    const cabinetMode = useTopMenuStore(
        state => state.cabinetMode
    )

    // Определение текущего URL (раздела в кабинете).
    const favoritesUrlMatch = useMatch(appUrl().cabinet.favorites)
    const notificationsUrlMatch = useMatch(appUrl().cabinet.notifications.index)
    const chatsUrlMatch = useMatch(appUrl().cabinet.chats.index)

    // Показывает окно входа, если пользователь не авторизован.
    // После успешного входа отправляет пользователя на запрошенный URL.
    const protectedOnClick = useEventCallback((
        e: MouseEvent<HTMLElement>
    ) => {
        const href: string | null = e.currentTarget.attributes.getNamedItem('href')?.value || null
        if (initUserAuth(href ? () => NavigationService.navigate(href) : null)) {
            e.preventDefault()
            return
        }
    })

    const showCabinetLinks: boolean = topMenuAuthBlock || topMenuCabinetMode
    const showCreateAdButton: boolean = (
        deviceType !== 'mobile'
        && bootstrapSize !== 'md'
        && createAdButton
        && (authBlock || cabinetMode)
    )

    return (
        <div className="flex-row-center justify-content-end top-nav-cabinet-buttons">
            <Collapse
                show={showCabinetLinks && topMenuCabinetMode}
                horizontal
            >
                {/* <div> нужен для правильной анимации в обе стороны. */}
                <div className="top-nav-collapsible-icon">
                    <NavLink
                        to={
                            AppConfig.features?.advances?.enabled
                                ? appUrl().cabinet.advancePayments.contract
                                : appUrl().cabinet.transactions.index
                        }
                        className="cabinet-link-icon with-icon-flex gap-2 link-muted link-hover-default"
                    >
                        <Icon
                            path={iconWallet}
                            size={headerIconSize}
                        />
                        <span className="d-none d-lg-inline text-nowrap">
                            {trans().cabinet.top_navigation.my_money}
                        </span>
                    </NavLink>
                </div>
            </Collapse>
            {showCabinetLinks && (
                <div className="top-nav-icons-group">
                    {!noFavorites && (
                        <IconHref
                            path={iconHeartOutline}
                            size={headerIconSize}
                            className={clsx(
                                'cabinet-link-icon',
                                favoritesUrlMatch ? 'active' : null
                            )}
                            tooltip={trans().common.top_navigation.liked_ads}
                            href={appUrl().cabinet.favorites}
                            onClick={protectedOnClick}
                        />
                    )}
                    <IconHref
                        path={hasNewNotifications ? iconBellWithCircle : iconBell}
                        size={headerIconSize}
                        className={clsx(
                            'cabinet-link-icon',
                            notificationsUrlMatch ? 'active' : null
                        )}
                        tooltip={trans().common.top_navigation.notifications}
                        href={appUrl().cabinet.notifications.index}
                        onClick={protectedOnClick}
                    />
                    <IconHref
                        path={hasNewChatMessages ? iconChatWithCircle : iconChat}
                        size={headerIconSize}
                        className={clsx(
                            'cabinet-link-icon',
                            chatsUrlMatch ? 'active' : null
                        )}
                        tooltip={trans().common.top_navigation.chats}
                        href={appUrl().cabinet.chats.index}
                        onClick={protectedOnClick}
                        color={hasNewChatMessages ? 'blue' : undefined}
                    />
                </div>
            )}
            {showCreateAdButton && (
                <div className="top-nav-add-listing-btn-wrapper">
                    <TopNavigationCreateAdButton />
                </div>
            )}
            <TopNavigationUserDropdownMenu className="top-nav-user-menu-toggle" />
        </div>
    )
}
